import { useCallback } from "react";
import { useTranslations } from "next-intl";
import { useFields } from "@/providers/FieldsProvider";
import { useGuestAutomationGate } from "@/hooks/useGuestAutomationGate";
import {
  buildEvalGuest,
  computeWorkflowOverwrites,
  GuestTrigger,
  OverwritePreview,
} from "@/lib/utils/workflow-overwrite-preview";

/**
 * Predicts how the event's enabled workflows will change a guest right after
 * save — the "an automation will overwrite what you typed" preview.
 *
 * Sits on top of {@link useGuestAutomationGate}, which owns the workflow query
 * and the ACTIONS plugin flag. The split matters: data providers only need the
 * gate's `mayRun`, and must not inherit this hook's `useFields` /
 * `useTranslations` requirements.
 *
 * `trigger` accepts a SET, because one write fires several: the caller's
 * explicit trigger plus every lifecycle trigger the backend derives from the
 * diff, each enqueued as its own message.
 */
export function useGuestWorkflowPreview(eventId: string | undefined) {
  const { allFields } = useFields();
  const tField = useTranslations("guest-fields");
  const { workflows, actionsPluginEnabled } = useGuestAutomationGate(eventId);

  const getOverwrites = useCallback(
    (
      fieldsValues: Record<string, unknown> & {
        additionalFields?: Record<string, unknown>;
      },
      previousValues:
        | (Record<string, unknown> & { additionalFields?: Record<string, unknown> })
        | null,
      trigger: GuestTrigger | GuestTrigger[],
    ): OverwritePreview => {
      const guest = buildEvalGuest(fieldsValues, allFields);
      const previousGuest = previousValues
        ? buildEvalGuest(previousValues, allFields)
        : null;
      return computeWorkflowOverwrites({
        workflows,
        fields: allFields,
        guest,
        // Raw entered values for the from-/conflict display: an untouched custom
        // default the admin never typed shouldn't read as a conflict on create.
        displayGuest: fieldsValues,
        previousGuest,
        triggers: Array.isArray(trigger) ? trigger : [trigger],
        actionsPluginEnabled,
        translateStandard: (name) => (tField.has(name) ? tField(name) : name),
      });
    },
    [workflows, allFields, tField, actionsPluginEnabled],
  );

  return { getOverwrites };
}
